import { useState } from 'react';

const TABS = [
  { id: 'arb',      label: '⚡ Arb' },
  { id: 'kelly',    label: '📐 Kelly' },
  { id: 'ev',       label: '📈 EV' },
  { id: 'novig',    label: '🎯 No-Vig' },
  { id: 'implied',  label: '🔢 Implied Prob' },
  { id: 'convert',  label: '🔄 Odds Converter' },
  { id: 'parlay',   label: '🎰 Parlay' },
];

function Card({ title, description, children }) {
  return (
    <div className="card" style={{ padding: 24 }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18, marginBottom: 8 }}>{title}</div>
        <div style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.6, padding: '12px 16px', background: 'rgba(0,229,176,0.04)', borderLeft: '3px solid var(--accent)', borderRadius: '0 var(--radius-sm) var(--radius-sm) 0' }}>
          {description}
        </div>
      </div>
      {children}
    </div>
  );
}

function InputRow({ label, value, onChange, placeholder, prefix, suffix, type = 'number' }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, fontFamily: 'var(--font-display)' }}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {prefix && <span style={{ fontSize: 13, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{prefix}</span>}
        <input
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          style={{ flex: 1, fontSize: 15, fontFamily: 'var(--font-display)', fontWeight: 600, padding: '10px 14px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text)', outline: 'none' }}
        />
        {suffix && <span style={{ fontSize: 13, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{suffix}</span>}
      </div>
    </div>
  );
}

function ResultBox({ label, value, highlight, sub }) {
  return (
    <div style={{ background: highlight ? 'rgba(0,229,176,0.08)' : 'var(--bg3)', border: `1px solid ${highlight ? 'rgba(0,229,176,0.3)' : 'var(--border)'}`, borderRadius: 'var(--radius-sm)', padding: '14px 16px', textAlign: 'center' }}>
      <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 22, color: highlight ? 'var(--accent)' : 'var(--text)' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function Divider() {
  return <div style={{ height: 1, background: 'var(--border)', margin: '20px 0' }} />;
}

// ── ARB CALCULATOR ─────────────────────────────────────────────────────────────
function ArbCalculator() {
  const [legs, setLegs] = useState([
    { odds: '', label: 'Home' },
    { odds: '', label: 'Draw' },
    { odds: '', label: 'Away' },
  ]);
  const [stake, setStake] = useState('100');

  const updateLeg = (i, field, val) => {
    const updated = [...legs];
    updated[i] = { ...updated[i], [field]: val };
    setLegs(updated);
  };

  const validLegs = legs.filter(l => parseFloat(l.odds) > 1);
  const bookPct = validLegs.length >= 2
    ? validLegs.reduce((sum, l) => sum + (1 / parseFloat(l.odds)), 0)
    : null;
  const isArb = bookPct !== null && bookPct < 1;
  const profitPct = bookPct !== null ? ((1 - bookPct) / bookPct) * 100 : null;
  const totalStake = parseFloat(stake) || 100;

  return (
    <Card
      title="⚡ Arbitrage Calculator"
      description="Enter odds from different bookmakers for each outcome of the same match. If the combined implied probability is under 100%, you have an arbitrage — a guaranteed profit regardless of the result. The calculator shows how to split your stake across bookmakers to lock in the return."
    >
      <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(3,1fr)', gap: 12, marginBottom: 16 }}>
        {legs.map((leg, i) => (
          <div key={i} style={{ background: 'var(--bg3)', borderRadius: 'var(--radius-sm)', padding: 14 }}>
            <input
              value={leg.label}
              onChange={e => updateLeg(i, 'label', e.target.value)}
              style={{ width: '100%', fontSize: 11, color: 'var(--text3)', background: 'transparent', border: 'none', marginBottom: 8, outline: 'none', textTransform: 'uppercase', letterSpacing: 0.5 }}
            />
            <input
              type="number"
              value={leg.odds}
              onChange={e => updateLeg(i, 'odds', e.target.value)}
              placeholder="e.g. 2.50"
              style={{ width: '100%', fontSize: 20, fontFamily: 'var(--font-display)', fontWeight: 700, background: 'transparent', border: 'none', color: 'var(--accent)', outline: 'none' }}
            />
            <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>
              {parseFloat(leg.odds) > 1 ? `Implied: ${(100 / parseFloat(leg.odds)).toFixed(1)}%` : 'Enter odds'}
            </div>
          </div>
        ))}
      </div>

      <InputRow label="Total Stake (BWP)" value={stake} onChange={setStake} placeholder="100" prefix="P" />

      {bookPct !== null && (
        <>
          <Divider />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 16 }}>
            <ResultBox label="Book %" value={`${(bookPct * 100).toFixed(2)}%`} highlight={isArb} sub={isArb ? '✓ Arb exists' : 'No arb'} />
            <ResultBox label="Profit %" value={isArb ? `+${profitPct.toFixed(2)}%` : '—'} highlight={isArb} />
            <ResultBox label="Guaranteed Return" value={isArb ? `P${(totalStake / bookPct).toFixed(2)}` : '—'} highlight={isArb} />
          </div>

          {isArb && (
            <>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Stake per leg</div>
              <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(3,1fr)', gap: 8 }}>
                {validLegs.map((leg, i) => (
                  <div key={i} style={{ background: 'rgba(0,229,176,0.06)', border: '1px solid rgba(0,229,176,0.2)', borderRadius: 'var(--radius-sm)', padding: 12, textAlign: 'center' }}>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>{leg.label}</div>
                    <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18, color: 'var(--accent)' }}>
                      P{((totalStake / bookPct) / parseFloat(leg.odds)).toFixed(2)}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>@ {leg.odds}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </Card>
  );
}

// ── KELLY CALCULATOR ───────────────────────────────────────────────────────────
function KellyCalculator() {
  const [odds, setOdds] = useState('');
  const [prob, setProb] = useState('');
  const [bankroll, setBankroll] = useState('1000');

  const o = parseFloat(odds);
  const p = parseFloat(prob) / 100;
  const b = parseFloat(bankroll) || 1000;

  const kelly = o > 1 && p > 0 && p < 1
    ? ((p * (o - 1) - (1 - p)) / (o - 1))
    : null;

  const halfKelly = kelly ? kelly / 2 : null;
  const quarterKelly = kelly ? kelly / 4 : null;

  return (
    <Card
      title="📐 Kelly Criterion"
      description="The Kelly Criterion tells you the mathematically optimal percentage of your bankroll to stake on a bet. It maximises long-term growth while protecting against ruin. Enter the decimal odds and your estimated probability of winning. Most professional bettors use Half Kelly (50% of the recommended stake) to reduce variance while maintaining most of the edge."
    >
      <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr 1fr', gap: 12 }}>
        <InputRow label="Decimal Odds" value={odds} onChange={setOdds} placeholder="e.g. 2.50" />
        <InputRow label="Your Win Probability" value={prob} onChange={setProb} placeholder="e.g. 45" suffix="%" />
        <InputRow label="Bankroll (BWP)" value={bankroll} onChange={setBankroll} placeholder="1000" prefix="P" />
      </div>

      {kelly !== null && (
        <>
          <Divider />
          {kelly <= 0 ? (
            <div style={{ padding: 16, background: 'rgba(240,71,71,0.08)', border: '1px solid rgba(240,71,71,0.2)', borderRadius: 'var(--radius-sm)', color: 'var(--danger)', fontSize: 13 }}>
              ⚠️ No edge — Kelly recommends not betting at these odds with this probability estimate.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
              <ResultBox label="Full Kelly" value={`P${(kelly * b).toFixed(2)}`} sub={`${(kelly * 100).toFixed(1)}% of bankroll`} />
              <ResultBox label="Half Kelly ✓" value={`P${(halfKelly * b).toFixed(2)}`} highlight sub={`${(halfKelly * 100).toFixed(1)}% of bankroll`} />
              <ResultBox label="Quarter Kelly" value={`P${(quarterKelly * b).toFixed(2)}`} sub={`${(quarterKelly * 100).toFixed(1)}% of bankroll`} />
            </div>
          )}
        </>
      )}
    </Card>
  );
}

// ── EV CALCULATOR ──────────────────────────────────────────────────────────────
function EVCalculator() {
  const [odds, setOdds] = useState('');
  const [prob, setProb] = useState('');
  const [stake, setStake] = useState('100');

  const o = parseFloat(odds);
  const p = parseFloat(prob) / 100;
  const s = parseFloat(stake) || 100;

  const ev = o > 1 && p > 0 && p < 1
    ? (p * (o - 1) * s) - ((1 - p) * s)
    : null;
  const evPct = ev !== null ? (ev / s) * 100 : null;
  const isPositive = ev !== null && ev > 0;

  return (
    <Card
      title="📈 Expected Value (EV)"
      description="Expected Value tells you how much you expect to win or lose on average per bet over thousands of repetitions. A positive EV bet (+EV) means the odds are in your favour — if you place this bet repeatedly, you will profit in the long run. Professional bettors only place +EV bets. Enter the odds, your estimated win probability, and your stake."
    >
      <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr 1fr', gap: 12 }}>
        <InputRow label="Decimal Odds" value={odds} onChange={setOdds} placeholder="e.g. 2.50" />
        <InputRow label="Win Probability" value={prob} onChange={setProb} placeholder="e.g. 45" suffix="%" />
        <InputRow label="Stake (BWP)" value={stake} onChange={setStake} placeholder="100" prefix="P" />
      </div>

      {ev !== null && (
        <>
          <Divider />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
            <ResultBox
              label="Expected Value"
              value={`${isPositive ? '+' : ''}P${ev.toFixed(2)}`}
              highlight={isPositive}
              sub={isPositive ? '✓ Positive EV' : '✗ Negative EV'}
            />
            <ResultBox label="EV %" value={`${isPositive ? '+' : ''}${evPct.toFixed(2)}%`} highlight={isPositive} />
            <ResultBox label="Expected Return" value={`P${(s + ev).toFixed(2)}`} sub="per bet on average" />
          </div>
        </>
      )}
    </Card>
  );
}

// ── NO-VIG CALCULATOR ──────────────────────────────────────────────────────────
function NoVigCalculator() {
  const [homeOdds, setHomeOdds] = useState('');
  const [drawOdds, setDrawOdds] = useState('');
  const [awayOdds, setAwayOdds] = useState('');

  const h = parseFloat(homeOdds);
  const d = parseFloat(drawOdds);
  const a = parseFloat(awayOdds);

  const hasAll = h > 1 && a > 1;
  const hasDraw = d > 1;

  let fairHome, fairDraw, fairAway, vig;

  if (hasAll) {
    const impliedH = 1 / h;
    const impliedD = hasDraw ? 1 / d : 0;
    const impliedA = 1 / a;
    const total = impliedH + impliedD + impliedA;
    vig = (total - 1) * 100;
    fairHome = 1 / (impliedH / total);
    fairDraw = hasDraw ? 1 / (impliedD / total) : null;
    fairAway = 1 / (impliedA / total);
  }

  return (
    <Card
      title="🎯 No-Vig Fair Odds"
      description="Bookmakers build a margin (the 'vig' or 'juice') into their odds so they profit regardless of the outcome. The No-Vig calculator strips out this margin to reveal the true fair odds — what the market actually thinks will happen. Compare a bookmaker's odds to the fair odds to spot value. If a bookmaker offers 3.0 but the fair odds are 2.6, that's a value bet."
    >
      <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr 1fr', gap: 12 }}>
        <InputRow label="Home Odds" value={homeOdds} onChange={setHomeOdds} placeholder="e.g. 2.10" />
        <InputRow label="Draw Odds (optional)" value={drawOdds} onChange={setDrawOdds} placeholder="e.g. 3.40" />
        <InputRow label="Away Odds" value={awayOdds} onChange={setAwayOdds} placeholder="e.g. 3.60" />
      </div>

      {hasAll && (
        <>
          <Divider />
          <div style={{ marginBottom: 12, padding: '10px 14px', background: 'rgba(240,71,71,0.06)', border: '1px solid rgba(240,71,71,0.15)', borderRadius: 'var(--radius-sm)', fontSize: 13 }}>
            Bookmaker margin: <strong style={{ color: 'var(--danger)' }}>{vig.toFixed(2)}%</strong>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: hasDraw ? 'repeat(3,1fr)' : '1fr 1fr', gap: 12 }}>
            <ResultBox label="Fair Home" value={fairHome.toFixed(3)} highlight sub={`${(100/fairHome).toFixed(1)}% prob`} />
            {fairDraw && <ResultBox label="Fair Draw" value={fairDraw.toFixed(3)} highlight sub={`${(100/fairDraw).toFixed(1)}% prob`} />}
            <ResultBox label="Fair Away" value={fairAway.toFixed(3)} highlight sub={`${(100/fairAway).toFixed(1)}% prob`} />
          </div>
        </>
      )}
    </Card>
  );
}

// ── IMPLIED PROBABILITY ────────────────────────────────────────────────────────
function ImpliedProbCalculator() {
  const [odds, setOdds] = useState('');

  const o = parseFloat(odds);
  const implied = o > 1 ? (1 / o) * 100 : null;

  return (
    <Card
      title="🔢 Implied Probability"
      description="Every set of odds implies a probability of that outcome occurring, as estimated by the bookmaker. Converting odds to probability helps you decide if a bet has value — if you believe the true probability is higher than the implied probability, the bet is worth taking. For example, odds of 3.00 imply a 33.3% chance. If you think the real chance is 40%, that is a value bet."
    >
      <InputRow label="Decimal Odds" value={odds} onChange={setOdds} placeholder="e.g. 2.50" />

      {implied !== null && (
        <>
          <Divider />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
            <ResultBox label="Implied Probability" value={`${implied.toFixed(2)}%`} highlight />
            <ResultBox label="Fractional" value={`${Math.round(o - 1 > 0 ? 1/(o-1)*10 : 0)}/10`} sub="approximate" />
            <ResultBox label="American" value={o >= 2 ? `+${Math.round((o - 1) * 100)}` : `-${Math.round(100 / (o - 1))}`} />
          </div>
        </>
      )}
    </Card>
  );
}

// ── ODDS CONVERTER ─────────────────────────────────────────────────────────────
function OddsConverter() {
  const [input, setInput] = useState('');
  const [type, setType] = useState('decimal');

  let decimal = null;
  const val = parseFloat(input);

  if (type === 'decimal' && val > 1) decimal = val;
  else if (type === 'american') {
    if (val > 0) decimal = (val / 100) + 1;
    else if (val < 0) decimal = (100 / Math.abs(val)) + 1;
  } else if (type === 'fractional') {
    const parts = input.split('/');
    if (parts.length === 2) {
      const num = parseFloat(parts[0]);
      const den = parseFloat(parts[1]);
      if (num > 0 && den > 0) decimal = (num / den) + 1;
    }
  }

  const american = decimal ? (decimal >= 2 ? `+${Math.round((decimal - 1) * 100)}` : `-${Math.round(100 / (decimal - 1))}`) : null;
  const fractional = decimal ? `${Math.round((decimal - 1) * 100)}/100` : null;
  const implied = decimal ? `${(100 / decimal).toFixed(2)}%` : null;

  return (
    <Card
      title="🔄 Odds Converter"
      description="Different bookmakers and countries display odds in different formats. Decimal odds (most common in Africa and Europe), American odds (moneyline, used in the USA), and fractional odds (traditional UK format) all express the same information differently. This converter lets you translate between formats instantly so you can compare odds across any bookmaker."
    >
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {['decimal', 'american', 'fractional'].map(t => (
          <button
            key={t}
            onClick={() => { setType(t); setInput(''); }}
            className={type === t ? 'btn btn-primary' : 'btn btn-outline'}
            style={{ fontSize: 12, padding: '6px 14px', textTransform: 'capitalize' }}
          >
            {t}
          </button>
        ))}
      </div>
      <InputRow
        label={`Enter ${type} odds`}
        value={input}
        onChange={setInput}
        placeholder={type === 'decimal' ? '2.50' : type === 'american' ? '+150 or -200' : '3/2'}
        type={type === 'fractional' ? 'text' : 'text'}
      />

      {decimal && (
        <>
          <Divider />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
            <ResultBox label="Decimal" value={decimal.toFixed(3)} highlight={type === 'decimal'} />
            <ResultBox label="American" value={american} highlight={type === 'american'} />
            <ResultBox label="Fractional" value={fractional} highlight={type === 'fractional'} />
            <ResultBox label="Implied Prob" value={implied} />
          </div>
        </>
      )}
    </Card>
  );
}

// ── PARLAY CALCULATOR ──────────────────────────────────────────────────────────
function ParlayCalculator() {
  const [legs, setLegs] = useState([{ odds: '', label: 'Leg 1' }, { odds: '', label: 'Leg 2' }]);
  const [stake, setStake] = useState('20');

  const addLeg = () => setLegs(l => [...l, { odds: '', label: `Leg ${l.length + 1}` }]);
  const removeLeg = i => setLegs(l => l.filter((_, j) => j !== i));
  const updateLeg = (i, field, val) => {
    const updated = [...legs];
    updated[i] = { ...updated[i], [field]: val };
    setLegs(updated);
  };

  const validLegs = legs.filter(l => parseFloat(l.odds) > 1);
  const combinedOdds = validLegs.reduce((acc, l) => acc * parseFloat(l.odds), 1);
  const s = parseFloat(stake) || 20;
  const payout = validLegs.length >= 2 ? combinedOdds * s : null;
  const profit = payout ? payout - s : null;

  return (
    <Card
      title="🎰 Parlay Calculator"
      description="A parlay (or accumulator) combines multiple bets into one, multiplying the odds together. All legs must win for you to collect. The combined odds create much larger potential payouts than single bets, but the more legs you add, the harder it is to win. Use this calculator to see your potential return before placing your multi-bet."
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
        {legs.map((leg, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              value={leg.label}
              onChange={e => updateLeg(i, 'label', e.target.value)}
              style={{ flex: 2, fontSize: 12, padding: '8px 12px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text)', outline: 'none' }}
              placeholder="Match name"
            />
            <input
              type="number"
              value={leg.odds}
              onChange={e => updateLeg(i, 'odds', e.target.value)}
              placeholder="Odds"
              style={{ flex: 1, fontSize: 15, fontFamily: 'var(--font-display)', fontWeight: 700, padding: '8px 12px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--accent)', outline: 'none' }}
            />
            {legs.length > 2 && (
              <button onClick={() => removeLeg(i)} style={{ color: 'var(--danger)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>✕</button>
            )}
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button onClick={addLeg} className="btn btn-outline" style={{ fontSize: 12, padding: '6px 14px' }}>
          + Add leg
        </button>
      </div>

      <InputRow label="Stake (BWP)" value={stake} onChange={setStake} placeholder="20" prefix="P" />

      {payout !== null && (
        <>
          <Divider />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
            <ResultBox label="Combined Odds" value={`${combinedOdds.toFixed(2)}x`} />
            <ResultBox label="Potential Return" value={`P${payout.toFixed(2)}`} highlight />
            <ResultBox label="Potential Profit" value={`P${profit.toFixed(2)}`} highlight sub={`from P${s} stake`} />
          </div>
          <div style={{ marginTop: 12, padding: '10px 14px', background: 'rgba(61,142,248,0.06)', borderRadius: 'var(--radius-sm)', fontSize: 12, color: 'var(--text3)' }}>
            💡 With {validLegs.length} legs, you need all {validLegs.length} to win. The implied probability of winning is {(validLegs.reduce((acc, l) => acc * (1/parseFloat(l.odds)), 1) * 100).toFixed(1)}%.
          </div>
        </>
      )}
    </Card>
  );
}

// ── MAIN PAGE ──────────────────────────────────────────────────────────────────
export default function Calculators() {
  const [tab, setTab] = useState('arb');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={tab === t.id ? 'btn btn-primary' : 'btn btn-outline'}
            style={{ fontSize: 12, padding: '7px 14px' }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Calculator content */}
      {tab === 'arb'     && <ArbCalculator />}
      {tab === 'kelly'   && <KellyCalculator />}
      {tab === 'ev'      && <EVCalculator />}
      {tab === 'novig'   && <NoVigCalculator />}
      {tab === 'implied' && <ImpliedProbCalculator />}
      {tab === 'convert' && <OddsConverter />}
      {tab === 'parlay'  && <ParlayCalculator />}
    </div>
  );
}